import * as React from 'react';
import {
  createFileRoute,
  notFound,
  Outlet,
  useMatchRoute,
  useNavigate,
} from '@tanstack/react-router';
import { ReactNode, useEffect, useMemo } from 'react';
import { Hierarchy } from '~/types';
import { AccordionMenu } from '~/components/ui/layout/navigation/submenu/AccordionMenu';
import { hierarchy2DocMenu } from '~/lib/fund/hierarchy2DocMenu';
import { Button } from 'primereact/button';
import { useAuth } from '@clerk/clerk-react';
import { useHierarchyEndpoint } from '~/api/useHierarchyEndpoint';
import { TableAgDocumentsGroup } from '~/components/ui/tables/ag/documentsGroupTable/TableAgDocumentsGroup';
import { Breadcrumb } from '~/components/ui/layout/navigation/breadcrumb/Breadcrumb';
import { BasicPage } from '~/components/ui/layout/page/basic/BasicPage';
import { HeaderContainer } from '~/components/ui/layout/page/HeaderContainer';
import { PageTagline } from '~/components/ui/layout/page/PageTagline';
import { PageContent } from '~/components/ui/layout/page/PageContent';
import { canAccess } from '~/utils/rbac';

export const Route = createFileRoute('/_authLayout/documents')({
  beforeLoad: async () => {
    if (!canAccess('document')) {
      throw notFound();
    }
  },
  component: DocumentsComponent,
});

function DocumentsComponent() {
  const [_menu, setMenu] = React.useState<ReactNode | null>(null);
  const matchRoute = useMatchRoute();
  const { fundHierarchy } = useHierarchyEndpoint();

  const isDashboard = matchRoute({ to: '/documents' });
  const isFund = useMemo(
    () => matchRoute({ to: '/documents/fund/$fundId' }),
    [matchRoute],
  );

  useEffect(() => {
    if (fundHierarchy) {
      let activeIndex: number | undefined;
      const meuItems = hierarchy2DocMenu(fundHierarchy);
      if (isFund) {
        activeIndex = meuItems.findIndex((item) =>
          item.links.find((link) => link.url.includes(isFund?.fundId)),
        );
      }
      setMenu(
        <AccordionMenu
          items={hierarchy2DocMenu(fundHierarchy)}
          activeIndex={activeIndex}
        />,
      );
    }
  }, [fundHierarchy, isFund]);

  return (
    <BasicPage title="Funds">
      {isDashboard ? (
        <JurisdictionTableContainer hierarchy={fundHierarchy} />
      ) : (
        <Outlet />
      )}
    </BasicPage>
  );
}
export type JurisdictionTableContainerProps = {
  hierarchy: Hierarchy[] | null;
};
const JurisdictionTableContainer = ({
  hierarchy,
}: JurisdictionTableContainerProps) => {
  const auth = useAuth();
  const navigate = useNavigate();
  if (!auth.isSignedIn && auth.isLoaded) {
    navigate({ to: '/sign-in' });
  }

  if (!hierarchy) {
    return <div>Loading...</div>;
  } else {
    return (
      <>
        <HeaderContainer>
          <Breadcrumb items={[{ name: 'Funds' }]} />
          <PageTagline message="List of funds grouped by jurisdiction" />
        </HeaderContainer>
        <PageContent>
          <div className="flex justify-between pt-2">
            <Button
              className="mb-3"
              onClick={() => navigate({ to: '/documents/fund-create' })}
            >
              Create fund
            </Button>
          </div>
          <TableAgDocumentsGroup hierarchy={hierarchy} />
        </PageContent>
      </>
    );
  }
};
